import {
  useCurrentTrustAccount,
  useTrustAccountPermissions,
  useTrustTypeStrategy
} from '@/features/trust-accounts/providers';

import {usePurchaseSummaryQuery} from './usePurchaseSummaryQuery';

export type AddBuyerTarget =
  | {kind: 'disabled'}
  | {kind: 'new-purchase'}
  | {kind: 'pending'}
  | {kind: 'error'}
  | {kind: 'missing-purchase'}
  | {kind: 'existing-purchase'; purchaseId: string};

/**
 * Where Add Buyer should send the lawyer. A trust type whose purchase is created up front at
 * activation (`createsPurchaseAtActivation`) always attaches to that existing purchase; every
 * other type creates a brand-new one per buyer.
 *
 * `'pending'`/`'error'`/`'missing-purchase'` are distinct query outcomes, not a shared fallback —
 * a still-loading summary must never be read as "no purchase exists yet" and open the wrong flow
 * (which would try to create a second purchase), and a failed or empty fetch must stay visibly
 * different from "still loading" so a caller isn't stuck disabled with no way to tell why.
 */
export function useAddBuyerTarget(): AddBuyerTarget {
  const {id: trustAccountId} = useCurrentTrustAccount();
  const {canCreateBuyer} = useTrustAccountPermissions();
  const {createsPurchaseAtActivation} = useTrustTypeStrategy();
  const {
    data: purchaseSummary,
    isLoading,
    isError
  } = usePurchaseSummaryQuery(trustAccountId, {
    enabled: canCreateBuyer && createsPurchaseAtActivation
  });

  if (!canCreateBuyer) return {kind: 'disabled'};
  if (!createsPurchaseAtActivation) return {kind: 'new-purchase'};
  if (isLoading) return {kind: 'pending'};
  if (isError) return {kind: 'error'};
  if (!purchaseSummary) return {kind: 'missing-purchase'};

  return {kind: 'existing-purchase', purchaseId: purchaseSummary.purchase.id};
}

export function isAddBuyerTargetActionable(target: AddBuyerTarget): boolean {
  return target.kind === 'new-purchase' || target.kind === 'existing-purchase';
}

/** Still resolving, or the query failed/came back empty — excludes `'disabled'`, which hides the
 * CTA entirely rather than disabling it. */
export function isAddBuyerTargetPending(target: AddBuyerTarget): boolean {
  return target.kind === 'pending' || target.kind === 'error' || target.kind === 'missing-purchase';
}
